import React, { useMemo, useCallback } from 'react'
import { Box } from '@mui/material'
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { ButtonSecondary, RecommendationsContainer } from '../../../interaxDesignSystemComponents'
import { SectionHeader } from './cards'
import {
  dontForgetTasksConfig,
  type DontForgetUser,
  type DontForgetTaskConfigItem,
  type DontForgetTaskConfigParams
} from './config/dontForgetConfig'

interface DontForgetTaskComponentProps {
  className?: string
  user?: DontForgetUser | null
  configOptions?: Omit<DontForgetTaskConfigParams, 'user'>
  onTaskNavigate?: (task: DontForgetTaskConfigItem) => void
}

/**
 * DontForgetTaskComponent
 *
 * Renders a list of actionable reminders for gamers.
 * Currently backed by a temporary config until the backend integration is ready.
 */
export const DontForgetTaskComponent: React.FC<DontForgetTaskComponentProps> = ({
  className,
  user,
  configOptions,
  onTaskNavigate
}) => {
  const { t } = useTranslation()

  const tasks = useMemo(
    () =>
      dontForgetTasksConfig({
        user,
        ...configOptions
      }),
    [configOptions, user]
  )

  const handleNavigate = useCallback(
    (task: DontForgetTaskConfigItem) => {
      if (onTaskNavigate) {
        onTaskNavigate(task)
      }
    },
    [onTaskNavigate]
  )

  return (
    <RecommendationsContainer className={className}>
      <SectionHeader title={t('gamer.dontForget.title')} subtitle={t('gamer.dontForget.subtitle')} />

      {tasks.length === 0 ? (
        // Empty state - all caught up
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            gap: '16px',
            width: '100%'
          }}
        >
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <CheckCircleIcon
              style={{
                width: '24px',
                height: '24px',
                color: INTERAX_COLORS.sun
              }}
            />
          </Box>
          <Box
            sx={{
              ...INTERAX_TYPOGRAPHY.body3,
              color: INTERAX_COLORS.typographyFont,
              textAlign: 'center'
            }}
          >
            {t('gamer.dontForget.empty', 'All caught up!')}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
          }}
        >
          {tasks.map((task) => (
            <Box
              key={task.id}
              sx={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: '16px',
                padding: '16px 16px 16px 29px',
                background: INTERAX_COLORS.heritageBlueRadial,
                border: `1px solid ${INTERAX_COLORS.white04}`,
                boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
                borderRadius: '10px'
              }}
            >
              <Box
                sx={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  border: `1.5px dashed ${INTERAX_COLORS.sun}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <ExclamationCircleIcon
                  style={{
                    width: '24px',
                    height: '24px',
                    color: INTERAX_COLORS.sun
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  flexGrow: 1
                }}
              >
                <Box
                  sx={{
                    fontSize: '18px',
                    lineHeight: '28px',
                    fontWeight: 400,
                    color: INTERAX_COLORS.white
                  }}
                >
                  {t(task.titleKey)}
                </Box>
              </Box>

              <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: 'flex-end' }}>
                <ButtonSecondary fullWidth disabled={!onTaskNavigate} onClick={() => handleNavigate(task)}>
                  {t(task.actionKey)}
                </ButtonSecondary>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </RecommendationsContainer>
  )
}

export default DontForgetTaskComponent
